if (!customElements.get('product-inventory')) {
  class ProductInventory extends HTMLElement {
    constructor() {
      super();
      window.initLazyScript(this, this.initLazySection.bind(this));
    }

    initLazySection() {
      this.inventoryNotice = this.querySelector('.js-inventory-notice');
      this.urgencyMessage = this.querySelector('.js-inventory-urgency');
      this.variantInventory = this.getVariantInventory();
      this.productInfo = this.closest('.product-info');
      this.form = this.productInfo?.querySelector('.js-product-form-main');
      this.qtyInput = this.form?.querySelector('[name="quantity"]');

      // Bind to on:variant:change event for this product
      const variantPicker = this.productInfo?.querySelector('variant-picker');
      if (variantPicker) {
        variantPicker.addEventListener('on:variant:change', this.handleVariantChange.bind(this));
      }

      // Re-sync when quantity changes (for partial backorders)
      if (this.form) {
        this.form.addEventListener('change', (e) => {
          if (e.target && e.target.name === 'quantity') this.syncBackorderProperty();
        });
        this.form.addEventListener('input', (e) => {
          if (e.target && e.target.name === 'quantity') this.syncBackorderProperty();
        });
      }

      // Init
      this.updateInventory(
        parseInt(this.dataset.inventoryQuantity, 10),
        this.dataset.variantAvailable === 'true',
        this.dataset.inventoryPolicy
      );
    }

    /**
     * Gets the inventory data for all product variants
     * @returns {?object}
     */
    getVariantInventory() {
      const dataEl = this.closest('.product-info').querySelector('.js-inventory-data');
      return this.variantInventory || JSON.parse(dataEl.textContent);
    }

    /**
     * Handles a 'change' event on the variant picker element
     * @param {object} evt - Event object
     */
    handleVariantChange(evt) {
      if (evt.detail.variant) {
        const inventory = this.variantInventory.filter(
          (variant) => variant.id === evt.detail.variant.id
        )[0];
        this.updateInventory(
          inventory.inventory_quantity,
          inventory.available,
          inventory.inventory_policy
        );
      } else {
        this.updateInventory(0, false);
        this.hidden = true;
      }
    }

    /**
     *
     * Updates the inventory notice
     * @param {number} count - the inventory quantity available
     * @param {boolean} available - whether current variant is available
     * @param {string} inventoryPolicy - whether product continues selling when out of stock
     */
    updateInventory(count, available, inventoryPolicy) {
      let inventoryLevel;
      this.currentInventoryQuantity = count;
      this.currentInventoryPolicy = inventoryPolicy;
      this.currentAvailable = available;

      if (count <= 0) {
        if (inventoryPolicy === 'continue') {
          inventoryLevel = 'backordered';
        } else if (available) {
          inventoryLevel = 'in_stock';
        } else {
          inventoryLevel = 'none';
        }
      } else if (count <= parseInt(this.dataset.thresholdVeryLow, 10)) {
        inventoryLevel = 'very_low';
      } else if (count <= parseInt(this.dataset.thresholdLow, 10)) {
        inventoryLevel = 'low';
      } else {
        inventoryLevel = 'normal';
      }

      if (((this.dataset.showNotice === 'always'
        || (this.dataset.showNotice === 'low' && inventoryLevel.includes('low')))
        && !(inventoryLevel === 'backordered' && this.dataset.showNoStockBackordered === 'false'))) {
        this.hidden = false;

        // Set the inventory level data attribute
        this.setAttribute('data-inventory-level', inventoryLevel);

        // Determine whether to show the count or not
        if (inventoryLevel === 'backordered') {
          this.inventoryNotice.innerText = '';
        } else if (inventoryLevel !== 'in_stock' && (this.dataset.showCount === 'always' || (this.dataset.showCount === 'low' && inventoryLevel.includes('low')))) {
          this.inventoryNotice.innerText = theme.strings.onlyXLeft.replace('[quantity]', count);
        } else if (inventoryLevel === 'very_low') {
          this.inventoryNotice.innerText = theme.strings.veryLowStock;
        } else if (inventoryLevel === 'low') {
          this.inventoryNotice.innerText = theme.strings.lowStock;
        } else if (inventoryLevel === 'normal' || inventoryLevel === 'in_stock') {
          this.inventoryNotice.innerText = theme.strings.inStock;
        } else if (inventoryLevel === 'none') {
          this.inventoryNotice.innerText = theme.strings.noStock;
        }

        // Update urgency message if needed
        if (this.urgencyMessage) {
          if (inventoryLevel === 'very_low') {
            this.urgencyMessage.innerHTML = this.dataset.textVeryLow;
          } else if (inventoryLevel === 'low') {
            this.urgencyMessage.innerHTML = this.dataset.textLow;
          } else if (inventoryLevel === 'backordered') {
            this.urgencyMessage.innerHTML = this.dataset.textNoStockBackordered;
          } else if (inventoryLevel === 'normal' || inventoryLevel === 'in_stock') {
            this.urgencyMessage.innerHTML = this.dataset.textNormal;
          } else if (inventoryLevel === 'none') {
            this.urgencyMessage.innerHTML = this.dataset.textNoStock;
          }
        }
      } else {
        this.hidden = true;
      }

      // Sync backordered hidden input on product form (for line item property)
      this.syncBackorderProperty();
    }

    /**
     * Add or remove the hidden inputs:
     * - properties[_backordered] = "yes" OR "Backordered X of Y"
     * @param {?string} backorderValue - value to set when backordered, or null to remove
     */
    updateBackorderedFormInput(backorderValue) {
      if (!this.form) return;

      const flagName = 'properties[_backordered]';
      let flagInput = this.form.querySelector(`input[name="${flagName}"]`);

      if (backorderValue) {
        if (!flagInput) {
          flagInput = document.createElement('input');
          flagInput.type = 'hidden';
          flagInput.name = flagName;
          this.form.appendChild(flagInput);
        }
        flagInput.value = backorderValue;
      } else {
        if (flagInput) flagInput.remove();
      }
    }

    /**
     * Computes and syncs partial backorder property based on selected quantity.
     * Example: inventory 3, qty 5 => _backordered="Backordered 2 of 5"
     */
    syncBackorderProperty() {
      if (!this.form) return;

      const qtyEl = this.form.querySelector('[name="quantity"]');
      const qty = Math.max(1, parseInt(qtyEl?.value ?? '1', 10) || 1);

      const count = parseInt(this.currentInventoryQuantity ?? '0', 10) || 0;
      const inventoryPolicy = this.currentInventoryPolicy;

      const inStock = Math.max(0, count);
      const isContinueSelling = inventoryPolicy === 'continue';
      const backorderQty = isContinueSelling ? Math.max(0, qty - inStock) : 0;

      // If fully backordered (no stock), keep a simple boolean marker.
      if (isContinueSelling && inStock <= 0) {
        this.updateBackorderedFormInput('yes');
        return;
      }

      if (backorderQty > 0) {
        this.updateBackorderedFormInput(`Backordered ${backorderQty} of ${qty}`);
      } else {
        this.updateBackorderedFormInput(null);
      }
    }
  }

  customElements.define('product-inventory', ProductInventory);
}
